import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Directive, EventEmitter, Inject, Input, OnDestroy, Output } from '@angular/core';
import { LogService, TypeShowLogInConsole } from '@app/core';
import { IndicatorService } from '@app/shared';
import { fromEvent, Observable, of, Subscription } from 'rxjs';
import { exhaustMap, filter, map, pairwise } from 'rxjs/operators';


interface ScrollPosition {
  sH: number;
  sT: number;
  cH: number;
};

const DEFAULT_SCROLL_POSITION: ScrollPosition = {
  sH: 0,
  sT: 0,
  cH: 0
};

@Directive({
  selector: '[Pagination]'
})
export class PaginationDirective implements AfterViewInit, OnDestroy {

  constructor(
    @Inject(DOCUMENT) private document: any,
    private readonly log: LogService,
    private readonly indicator: IndicatorService) { }

  @Input() isEnd: boolean = false;
  @Input('disable-when-fetch-data') isLoading: boolean = false;
  @Output() goTo: EventEmitter<number> = new EventEmitter<number>();
  @Input() private page: number = 0;

  private subscription: Subscription;
  private scrollEvent$: Observable<any>;
  private userScrolledDown$: Observable<any>;
  private requestOnScroll$: Observable<any>;

  public ngAfterViewInit() {
    this.registerScrollEvent();
    this.streamScrollEvents();
    this.requestCallbackOnScroll();
  }

  private registerScrollEvent() {
    this.scrollEvent$ = fromEvent(this.document, 'scroll');
  }

  private streamScrollEvents() {
    this.userScrolledDown$ = this.scrollEvent$.pipe(
      map((e: any): ScrollPosition => ({
        sH: e.target.scrollingElement.scrollHeight,
        sT: e.target.scrollingElement.scrollTop,
        cH: e.target.scrollingElement.clientHeight
      })),
      pairwise(),
      filter(positions => !this.isEnd && !this.isLoading && this.isUserScrollingDown(positions) && this.isScrollExpectedPercent(positions[1])))
  }

  private scrollCallbacks() {
    return of([]);
  }

  private requestCallbackOnScroll() {
    this.requestOnScroll$ = this.userScrolledDown$;
    this.subscription = this.requestOnScroll$.pipe(
      exhaustMap(() => {
        ++this.page;
        return this.scrollCallbacks();
      })
    ).subscribe(() => {
      this.indicator.showHude();
      this.goTo.emit(this.page);
    }, (err) => this.log.logger(err, TypeShowLogInConsole.error));
  }

  private isUserScrollingDown = (positions) => {
    return positions[0].sT < positions[1].sT;
  }

  private isScrollExpectedPercent = (position) => {
    return !this.isEnd && ((position.sT + position.cH) + 150) >= position.sH;
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
